import React, { useState, useEffect } from 'react';
import Layout from '../../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { psAPI, companyAPI, financeAPI } from '../../../services/api';
import { toast } from 'sonner';
import { Plus, Edit, ArrowLeft, Settings } from 'lucide-react';
import { RecordActions } from '../../../components/common/RecordActions';
import { RecordViewDialog } from '../../../components/common/RecordViewDialog';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const TRANSACTION_TYPES = [
  { value: 'PROJECT_COST', label: 'Project Cost' },
  { value: 'LABOR_COST', label: 'Labor Cost' },
  { value: 'MATERIAL_COST', label: 'Material Cost' },
  { value: 'EXTERNAL_SERVICE', label: 'External Service' },
  { value: 'OVERHEAD', label: 'Overhead' },
  { value: 'WIP_COST', label: 'WIP Cost' },
  { value: 'WIP_REVENUE', label: 'WIP Revenue' },
  { value: 'SETTLEMENT_COST', label: 'Settlement Cost' },
  { value: 'SETTLEMENT_REVENUE', label: 'Settlement Revenue' },
  { value: 'INVENTORY_CONSUMPTION', label: 'Inventory Consumption' },
  { value: 'LABOR_CLEARING', label: 'Labor Clearing' },
];

const PROJECT_TYPES = [
  { value: 'ALL', label: 'All Project Types' },
  { value: 'INTERNAL', label: 'Internal' },
  { value: 'CUSTOMER', label: 'Customer' },
  { value: 'INVESTMENT', label: 'Investment' },
  { value: 'MAINTENANCE', label: 'Maintenance' },
  { value: 'RD', label: 'R&D' },
];

export default function AccountDetermination() {
  const navigate = useNavigate();
  const [determinations, setDeterminations] = useState([]);
  const [companyCodes, setCompanyCodes] = useState([]);
  const [glAccounts, setGLAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingDet, setEditingDet] = useState(null);
  const { canWrite } = useAuth();
  const writable = canWrite('accountdetermination');
  const [viewItem, setViewItem] = useState(null);
  const [formData, setFormData] = useState({
    company_code: '',
    transaction_type: '',
    project_type: '',
    debit_account: '',
    credit_account: '',
    cost_element: '',
    profit_center: '',
    description: '',
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [detsRes, companiesRes] = await Promise.all([
        psAPI.getAccountDeterminations(),
        companyAPI.getCompanyCodes()
      ]);
      setDeterminations(detsRes.data);
      setCompanyCodes(companiesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load account determinations');
    } finally {
      setLoading(false);
    }
  };

  const fetchGLAccounts = async (companyCodeId) => {
    try {
      const coaRes = await financeAPI.getChartOfAccounts();
      const coas = coaRes.data;
      
      // Get GL accounts from all COAs (simplified - in production, filter by company code assignment)
      let allAccounts = [];
      for (const coa of coas) {
        try {
          const accountsRes = await financeAPI.getGLAccountsInCOA(coa.coa_id);
          allAccounts = [...allAccounts, ...accountsRes.data];
        } catch (e) {
          // Ignore errors for individual COAs
        }
      }
      setGLAccounts(allAccounts);
    } catch (error) {
      console.error('Error fetching GL accounts:', error);
    }
  };

  const handleCreate = () => {
    setEditingDet(null);
    setFormData({
      company_code: '',
      transaction_type: '',
      project_type: 'ALL',
      debit_account: '',
      credit_account: '',
      cost_element: '',
      profit_center: '',
      description: '',
      is_active: true
    });
    setShowDialog(true);
  };

  const handleEdit = (det) => {
    setEditingDet(det);
    setFormData({
      company_code: det.company_code,
      transaction_type: det.transaction_type,
      project_type: det.project_type || 'ALL',
      debit_account: det.debit_account,
      credit_account: det.credit_account,
      cost_element: det.cost_element || '',
      profit_center: det.profit_center || '',
      description: det.description || '',
      is_active: det.is_active
    });
    fetchGLAccounts(det.company_code);
    setShowDialog(true);
  };

  const handleCompanyCodeChange = (value) => {
    setFormData({ ...formData, company_code: value });
    fetchGLAccounts(value);
  };

  const handleSubmit = async () => {
    if (!formData.company_code || !formData.transaction_type || !formData.debit_account || !formData.credit_account) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const data = {
        ...formData,
        project_type: formData.project_type === 'ALL' ? null : (formData.project_type || null)
      };

      if (editingDet) {
        await psAPI.updateAccountDetermination(editingDet.determination_id, data);
        toast.success('Account determination updated');
      } else {
        await psAPI.createAccountDetermination(data);
        toast.success('Account determination created');
      }
      setShowDialog(false);
      fetchData();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error(error.response?.data?.detail || 'Failed to save account determination');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="account-determination-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/ps/integration')}
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">PS Account Determination</h1>
              <p className="text-gray-500 mt-1">Configure GL account mappings for PS-FI integration</p>
            </div>
          </div>
          {writable && (<Button onClick={handleCreate} data-testid="create-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Rule
          </Button>)}
        </div>

        {/* Rules Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Settings className="h-5 w-5 text-orange-600" />
              Account Determination Rules ({determinations.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {determinations.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Settings className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <p>No account determination rules configured</p>
                <p className="text-sm mt-1">Create rules to automate PS-FI postings</p>
                {writable && (<Button className="mt-4" onClick={handleCreate}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create First Rule
                </Button>)}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3 font-medium">Company Code</th>
                      <th className="text-left p-3 font-medium">Transaction Type</th>
                      <th className="text-left p-3 font-medium">Project Type</th>
                      <th className="text-left p-3 font-medium">Debit Account</th>
                      <th className="text-left p-3 font-medium">Credit Account</th>
                      <th className="text-left p-3 font-medium">Status</th>
                      <th className="text-center p-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {determinations.map((det) => (
                      <tr key={det.determination_id} className="border-b hover:bg-gray-50">
                        <td className="p-3 font-medium">{det.company_code}</td>
                        <td className="p-3">
                          <Badge variant="outline">
                            {TRANSACTION_TYPES.find(t => t.value === det.transaction_type)?.label || det.transaction_type}
                          </Badge>
                        </td>
                        <td className="p-3 text-gray-500">
                          {det.project_type || 'All'}
                        </td>
                        <td className="p-3 font-mono">{det.debit_account}</td>
                        <td className="p-3 font-mono">{det.credit_account}</td>
                        <td className="p-3">
                          {det.is_active ? (
                            <Badge className="bg-green-100 text-green-800">Active</Badge>
                          ) : (
                            <Badge className="bg-gray-100 text-gray-800">Inactive</Badge>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          <RecordActions doctype="accountdetermination" onView={() => setViewItem(det)} onEdit={() => handleEdit(det)} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingDet ? 'Edit Account Determination' : 'Create Account Determination'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Company Code *</Label>
                  <Select
                    value={formData.company_code}
                    onValueChange={handleCompanyCodeChange}
                    disabled={!!editingDet}
                  >
                    <SelectTrigger data-testid="company-code-select">
                      <SelectValue placeholder="Select company code" />
                    </SelectTrigger>
                    <SelectContent>
                      {companyCodes.map(cc => (
                        <SelectItem key={cc.company_code_id} value={cc.company_code_id}>
                          {cc.company_code_id} - {cc.company_code_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Transaction Type *</Label>
                  <Select
                    value={formData.transaction_type}
                    onValueChange={(v) => setFormData({ ...formData, transaction_type: v })}
                    disabled={!!editingDet}
                  >
                    <SelectTrigger data-testid="transaction-type-select">
                      <SelectValue placeholder="Select transaction type" />
                    </SelectTrigger>
                    <SelectContent>
                      {TRANSACTION_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Project Type (Optional)</Label>
                <Select
                  value={formData.project_type}
                  onValueChange={(v) => setFormData({ ...formData, project_type: v })}
                >
                  <SelectTrigger data-testid="project-type-select">
                    <SelectValue placeholder="All project types" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROJECT_TYPES.map(pt => (
                      <SelectItem key={pt.value} value={pt.value}>
                        {pt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Debit GL Account *</Label>
                  <Input
                    value={formData.debit_account}
                    onChange={(e) => setFormData({ ...formData, debit_account: e.target.value })}
                    placeholder="e.g., 600000"
                    data-testid="debit-account-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Credit GL Account *</Label>
                  <Input
                    value={formData.credit_account}
                    onChange={(e) => setFormData({ ...formData, credit_account: e.target.value })}
                    placeholder="e.g., 200000"
                    data-testid="credit-account-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Cost Element (Optional)</Label>
                  <Input
                    value={formData.cost_element}
                    onChange={(e) => setFormData({ ...formData, cost_element: e.target.value })}
                    placeholder="Cost element override"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Profit Center (Optional)</Label>
                  <Input
                    value={formData.profit_center}
                    onChange={(e) => setFormData({ ...formData, profit_center: e.target.value })}
                    placeholder="Profit center override"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Description of this rule"
                  data-testid="description-input"
                />
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  data-testid="active-switch"
                />
                <Label>Active</Label>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} data-testid="save-btn">
                {editingDet ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
}
